import React, { useEffect, useState, useMemo } from 'react';
import { useParams, useNavigate, Link, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import {
  ArrowLeftIcon,
  UserIcon,
  CheckCircleIcon,
  XMarkIcon,
  ChatBubbleLeftIcon,
  EyeIcon,
  CalendarDaysIcon,
  MapPinIcon,
  MagnifyingGlassIcon,
  FunnelIcon,
  ExclamationTriangleIcon
} from '@heroicons/react/24/outline';
import { formatDistanceToNow } from 'date-fns';
import { useAppSelector } from '../hooks/redux';
import { opportunityAPI } from '../services/api';
import { OpportunityApplication, Opportunity } from '../types';
import Button from '../components/common/Button';
import Avatar from '../components/common/Avatar';
import MasonryCollabGrid from '../components/profile/MasonryCollabGrid';
import toast from 'react-hot-toast';

const ReviewApplicantsPage: React.FC = () => {
  const { opportunityId } = useParams<{ opportunityId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const { user } = useAppSelector((state) => state.auth);

  const [opportunity, setOpportunity] = useState<Opportunity | null>(null);
  const [applications, setApplications] = useState<OpportunityApplication[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  
  // Selection & Filtering
  const [selectedApplicantId, setSelectedApplicantId] = useState<string | null>(null);
  const [filterStatus, setFilterStatus] = useState<'all' | 'pending' | 'accepted' | 'rejected'>('all');
  const [searchQuery, setSearchQuery] = useState('');

  // Modals
  const [showRejectModal, setShowRejectModal] = useState(false);
  const [showAcceptModal, setShowAcceptModal] = useState(false);
  const [actionMessage, setActionMessage] = useState('');
  const [processing, setProcessing] = useState(false);

  useEffect(() => {
    if (opportunityId) {
      fetchApplications(1);
    }
  }, [opportunityId]);

  // Handle auto-selection from navigation state
  useEffect(() => {
    if (location.state?.selectedApplicationId && applications.length > 0) {
      setSelectedApplicantId(location.state.selectedApplicationId);
      // Clear state to prevent re-selection on refresh/navigation
      window.history.replaceState({}, document.title);
    }
  }, [location.state, applications]);

  const fetchApplications = async (pageNum = 1) => {
    try {
      if (pageNum === 1) setLoading(true);
      else setLoadingMore(true);

      const response = await opportunityAPI.getOpportunityApplications(opportunityId!, pageNum);
      const newApplications = response.data.data.applications;
      
      if (pageNum === 1) {
        setApplications(newApplications);
        setOpportunity(response.data.data.opportunity);
      } else {
        setApplications(prev => [...prev, ...newApplications]);
      }

      setHasMore(newApplications.length === 20); // Assuming limit is 20
      setPage(pageNum);
      
      // Select first application by default if none selected
      if (pageNum === 1 && newApplications.length > 0 && !selectedApplicantId) {
        // setSelectedApplicantId(newApplications[0].id);
      }
    } catch (error: any) {
      console.error('Failed to fetch applications:', error);
      toast.error('Failed to load applications');
      if (pageNum === 1) navigate('/opportunities/my');
    } finally {
      setLoading(false);
      setLoadingMore(false);
    }
  };

  const filteredApplications = useMemo(() => {
    return applications.filter(app => {
      const matchesStatus = filterStatus === 'all' || app.status === filterStatus;
      const matchesSearch = (
        app.applicant?.first_name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        app.applicant?.last_name?.toLowerCase().includes(searchQuery.toLowerCase())
      );
      return matchesStatus && matchesSearch;
    });
  }, [applications, filterStatus, searchQuery]);

  const selectedApplication = useMemo(() => {
    return applications.find(app => app.id === selectedApplicantId);
  }, [applications, selectedApplicantId]);

  const handleAcceptApplication = async () => {
    if (!selectedApplicantId) return;
    try {
      setProcessing(true);
      await opportunityAPI.updateApplicationStatus(selectedApplicantId, {
        status: 'accepted',
        message: actionMessage
      });
      
      toast.success('Application accepted successfully!');
      setShowAcceptModal(false);
      setActionMessage('');
      setActionMessage('');
      fetchApplications(1);
    } catch (error: any) {
      console.error('Failed to accept application:', error);
      toast.error('Failed to accept application');
    } finally {
      setProcessing(false);
    }
  };

  const handleRejectApplication = async () => {
    if (!selectedApplicantId) return;
    try {
      setProcessing(true);
      await opportunityAPI.updateApplicationStatus(selectedApplicantId, {
        status: 'rejected',
        message: actionMessage
      });
      
      toast.success('Application rejected');
      setShowRejectModal(false);
      setActionMessage('');
      setActionMessage('');
      fetchApplications(1);
    } catch (error: any) {
      console.error('Failed to reject application:', error);
      toast.error('Failed to reject application');
    } finally {
      setProcessing(false);
    }
  };

  const handleMessageApplicant = (applicant: any) => {
    navigate('/messages', {
      state: {
        openConversationWith: applicant.id,
        userName: `${applicant.first_name} ${applicant.last_name}`,
        userAvatar: applicant.profile_pic,
        userType: applicant.user_type,
        prefilledMessage: generateApplicantMessage(applicant, opportunity!),
        opportunityContext: {
          id: opportunity!.id,
          title: opportunity!.title,
          type: opportunity!.opportunity_type
        }
      }
    });
  };

  const generateApplicantMessage = (applicant: any, opportunity: Opportunity) => {
    return `Hi ${applicant.first_name}! 👋\n\nThank you for your interest in "${opportunity.title}". I'd like to discuss your application and learn more about your experience.\n\nLooking forward to chatting with you!\n\nBest regards,\n${user?.first_name}`;
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'accepted': return 'bg-green-100 text-green-800 border-green-200';
      case 'rejected': return 'bg-red-100 text-red-800 border-red-200';
      case 'pending': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  if (loading) {
    return (
      <div className="flex h-[calc(100vh-64px)] bg-white dark:bg-gray-900">
        <div className="w-96 border-r border-gray-200 p-4 space-y-4">
          <div className="h-10 bg-gray-100 rounded animate-pulse" />
          {[...Array(5)].map((_, i) => (
            <div key={i} className="h-24 bg-gray-100 rounded animate-pulse" />
          ))}
        </div>
        <div className="flex-1 p-8 space-y-6">
          <div className="h-32 bg-gray-100 rounded animate-pulse" />
          <div className="h-64 bg-gray-100 rounded animate-pulse" />
        </div>
      </div>
    );
  }

  if (!opportunity) return null;

  return (
    <div className="fixed inset-0 top-16 md:left-64 flex bg-gray-50 dark:bg-gray-900 overflow-hidden">
      {/* Left Sidebar - List */}
      <div className={`w-full md:w-96 bg-white dark:bg-gray-900 border-r border-gray-200 dark:border-gray-800 flex-col flex-shrink-0 z-10 h-full overflow-hidden ${
        selectedApplicantId ? 'hidden md:flex' : 'flex'
      }`}>
        {/* Header */}
        <div className="p-4 border-b border-gray-200 dark:border-gray-800 bg-white dark:bg-gray-900">
          <div className="flex items-center gap-2 mb-4">
            <button
              onClick={() => navigate(`/opportunities/${opportunityId}`)}
              className="p-2 -ml-2 hover:bg-gray-100 dark:hover:bg-gray-800 rounded-full text-gray-500 dark:text-gray-400 transition-colors"
            >
              <ArrowLeftIcon className="h-5 w-5" />
            </button>
            <h1 className="font-bold text-gray-900 dark:text-white truncate">
              {opportunity.title}
            </h1>
          </div>

          {/* Search */}
          <div className="relative mb-4">
            <MagnifyingGlassIcon className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Search applicants..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-9 pr-4 py-2 bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg text-sm focus:ring-2 focus:ring-teal-500 focus:border-transparent text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
          </div>

          {/* Filters */}
          <div className="flex gap-2 overflow-x-auto pb-2 scrollbar-hide">
            {(['all', 'pending', 'accepted', 'rejected'] as const).map((status) => (
              <button
                key={status}
                onClick={() => setFilterStatus(status)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium whitespace-nowrap transition-colors ${
                  filterStatus === status
                    ? 'bg-teal-600 text-white'
                    : 'bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300 hover:bg-gray-200 dark:hover:bg-gray-700'
                }`}
              >
                {status.charAt(0).toUpperCase() + status.slice(1)}
              </button>
            ))}
          </div>
        </div>

        {/* List */}
        <div 
            className="flex-1 overflow-y-auto min-h-0"
            onScroll={(e) => {
                const target = e.currentTarget;
                if (
                    target.scrollHeight - target.scrollTop <= target.clientHeight + 400 &&
                    hasMore &&
                    !loadingMore &&
                    !loading
                ) {
                    fetchApplications(page + 1);
                }
            }}
        >
          {filteredApplications.length > 0 ? (
            <div className="divide-y divide-gray-100">
              {filteredApplications.map((app) => (
                <button
                  key={app.id}
                  onClick={() => setSelectedApplicantId(app.id)}
                  className={`w-full p-4 flex items-start gap-3 text-left transition-colors hover:bg-gray-50 dark:hover:bg-gray-800/50 ${
                    selectedApplicantId === app.id ? 'bg-teal-50 dark:bg-teal-900/20 border-r-2 border-teal-500' : ''
                  }`}
                >
                  <Avatar
                    src={app.applicant?.profile_pic_signed || app.applicant?.profile_pic}
                    alt={app.applicant?.first_name || 'Applicant'}
                    size="md"
                  />
                  <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start mb-1">
                      <h3 className={`font-medium truncate ${
                        selectedApplicantId === app.id ? 'text-teal-900 dark:text-teal-300' : 'text-gray-900 dark:text-white'
                      }`}>
                        {app.applicant?.first_name} {app.applicant?.last_name}
                      </h3>
                      <span className="text-xs text-gray-500 flex-shrink-0">
                        {formatDistanceToNow(new Date(app.applied_at), { addSuffix: true })}
                      </span>
                    </div>
                    <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400 mb-2">
                      <span className="capitalize">{app.applicant?.user_type}</span>
                      {app.applicant?.location && (
                        <>
                          <span>•</span>
                          <span className="truncate">{app.applicant.location}</span>
                        </>
                      )}
                    </div>
                    <span className={`inline-flex px-2 py-0.5 rounded text-[10px] font-medium border ${getStatusColor(app.status)}`}>
                      {app.status.charAt(0).toUpperCase() + app.status.slice(1)}
                    </span>
                  </div>
                </button>
              ))}
              </div>
          ) : (
            <div className="p-8 text-center text-gray-500">
              <UserIcon className="h-12 w-12 mx-auto text-gray-300 mb-2" />
              <p>No applicants found</p>
            </div>
          )}
          
          {loadingMore && (
            <div className="flex justify-center py-4">
                <div className="animate-spin rounded-full h-5 w-5 border-2 border-gray-200 border-t-teal-500"></div>
            </div>
          )}
        </div>
      </div>

      {/* Right Panel - Detail View */}
      <div className={`flex-col overflow-hidden bg-gray-50 dark:bg-gray-900 ${
        selectedApplicantId 
          ? 'fixed inset-0 top-16 z-30 flex w-full md:relative md:top-auto md:inset-auto md:z-auto md:flex-1 md:w-auto md:h-full' 
          : 'hidden md:flex md:flex-1 md:relative md:h-full'
      }`}>
        {selectedApplication ? (
          <>
            {/* Fixed Profile Header */}
            <div className="p-6 md:p-8 pb-0 shrink-0 z-10">
              <div className="max-w-4xl mx-auto">
                <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700 flex flex-col md:flex-row items-start md:items-center gap-6 relative">
                  {/* Mobile Back Button */}
                  <button
                    onClick={() => setSelectedApplicantId(null)}
                    className="md:hidden absolute top-4 right-4 p-2 text-gray-400 hover:text-gray-600"
                  >
                    <XMarkIcon className="h-6 w-6" />
                  </button>
                  <Avatar
                    src={selectedApplication.applicant?.profile_pic_signed || selectedApplication.applicant?.profile_pic}
                    alt={selectedApplication.applicant?.first_name || 'Applicant'}
                    size="xl"
                    className="w-24 h-24 ring-4 ring-gray-50"
                  />
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-2">
                      <h2 className="text-2xl font-bold text-gray-900 dark:text-white">
                        {selectedApplication.applicant?.first_name} {selectedApplication.applicant?.last_name}
                      </h2>
                      <Link to={`/profile/${selectedApplication.applicant?.id}`}>
                        <Button variant="ghost" size="sm" className="text-teal-600">
                          <EyeIcon className="h-4 w-4 mr-1" />
                          View Profile
                        </Button>
                      </Link>
                    </div>
                    
                    <div className="flex flex-wrap gap-4 text-sm text-gray-600 dark:text-gray-300">
                      <div className="flex items-center gap-1">
                        <UserIcon className="h-4 w-4" />
                        <span className="capitalize">{selectedApplication.applicant?.user_type}</span>
                      </div>
                      {selectedApplication.applicant?.location && (
                        <div className="flex items-center gap-1">
                          <MapPinIcon className="h-4 w-4" />
                          <span>{selectedApplication.applicant.location}</span>
                        </div>
                      )}
                      <div className="flex items-center gap-1">
                        <CalendarDaysIcon className="h-4 w-4" />
                        <span>Applied {formatDistanceToNow(new Date(selectedApplication.applied_at), { addSuffix: true })}</span>
                      </div>
                    </div>
                  </div>

                  <div className={`px-4 py-2 rounded-lg border ${getStatusColor(selectedApplication.status)}`}>
                    <span className="font-bold tracking-wide text-sm">
                      {selectedApplication.status.charAt(0).toUpperCase() + selectedApplication.status.slice(1)}
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Scrollable Detail Content */}
            <div className="flex-1 overflow-y-auto p-6 md:p-8 pt-6">
              <div className="max-w-4xl mx-auto space-y-8">

                {/* Application Message */}
                {selectedApplication.message && (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4 flex items-center gap-2">
                      <ChatBubbleLeftIcon className="h-5 w-5 text-gray-400" />
                      Application Message
                    </h3>
                    <div className="bg-gray-50 dark:bg-gray-700/50 rounded-xl p-4 text-gray-700 dark:text-gray-300 whitespace-pre-wrap leading-relaxed">
                      {selectedApplication.message}
                    </div>
                  </div>
                )}

                {/* Portfolio */}
                {selectedApplication.portfolio_items && selectedApplication.portfolio_items.length > 0 && (
                  <div className="bg-white dark:bg-gray-800 rounded-2xl p-6 shadow-sm border border-gray-200 dark:border-gray-700">
                    <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-4">Portfolio Preview</h3>
                    <MasonryCollabGrid 
                      collabImages={selectedApplication.portfolio_items} 
                      isOwnProfile={false} 
                    />
                  </div>
                )}
              </div>
            </div>

            {/* Sticky Action Footer */}
            <div className="bg-white dark:bg-gray-800 border-t border-gray-200 dark:border-gray-700 p-4 flex items-center justify-between gap-4 z-20 shadow-[0_-4px_6px_-1px_rgba(0,0,0,0.05)] shrink-0">
              <div className="hidden md:block text-sm text-gray-500">
                Reviewing {selectedApplication.applicant?.first_name}'s application
              </div>
              
              <div className="grid grid-cols-3 md:flex items-center gap-2 md:gap-3 flex-1 md:flex-none justify-end w-full md:w-auto">
                <Button
                  variant="outline"
                  onClick={() => handleMessageApplicant(selectedApplication.applicant)}
                  className="justify-center px-2 md:px-4"
                >
                  <ChatBubbleLeftIcon className="h-5 w-5 md:mr-2" />
                  <span className="hidden md:inline">Message</span>
                  <span className="md:hidden">Chat</span>
                </Button>

                {(selectedApplication.status === 'pending' || selectedApplication.status === 'accepted') && (
                  <Button
                    variant="outline"
                    className="text-red-600 border-red-200 hover:bg-red-50 justify-center px-2 md:px-4"
                    onClick={() => setShowRejectModal(true)}
                  >
                    <XMarkIcon className="h-5 w-5 md:mr-2" />
                    <span className="hidden md:inline">Reject</span>
                    <span className="md:hidden">Reject</span>
                  </Button>
                )}

                {(selectedApplication.status === 'pending' || selectedApplication.status === 'rejected') && (
                  <Button
                    variant="primary"
                    className="bg-green-600 hover:bg-green-700 justify-center px-2 md:px-4"
                    onClick={() => setShowAcceptModal(true)}
                  >
                    <CheckCircleIcon className="h-5 w-5 md:mr-2" />
                    <span className="hidden md:inline">Accept</span>
                    <span className="md:hidden">Accept</span>
                  </Button>
                )}
              </div>
            </div>
          </>
        ) : (
          <div className="flex-1 flex flex-col items-center justify-center text-gray-400 p-8">
            <div className="w-24 h-24 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mb-6">
              <UserIcon className="h-12 w-12 text-gray-300 dark:text-gray-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 dark:text-white mb-2">Select an applicant</h2>
            <p className="text-center max-w-sm">
              Choose an applicant from the list to view their details, portfolio, and take action.
            </p>
          </div>
        )}
      </div>

      {/* Accept Modal */}
      <AnimatePresence>
        {showAcceptModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-green-100 dark:bg-green-900/30 rounded-full flex items-center justify-center">
                    <CheckCircleIcon className="h-6 w-6 text-green-600 dark:text-green-400" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Accept Application</h3>
                </div>
                
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Send a message to <strong>{selectedApplication?.applicant?.first_name}</strong>. This will also start a chat conversation.
                </p>
                
                <textarea
                  value={actionMessage}
                  onChange={(e) => setActionMessage(e.target.value)}
                  placeholder="Congratulations! I'd love to work with you..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-green-500 focus:border-transparent mb-6 resize-none"
                />
                
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowAcceptModal(false)} className="dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">
                    Cancel
                  </Button>
                  <Button
                    variant="primary"
                    onClick={handleAcceptApplication}
                    loading={processing}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    Accept & Message
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50 backdrop-blur-sm">
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.95 }}
              className="bg-white dark:bg-gray-800 rounded-xl shadow-xl max-w-md w-full overflow-hidden"
            >
              <div className="p-6">
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-10 h-10 bg-red-100 dark:bg-red-900/30 rounded-full flex items-center justify-center">
                    <XMarkIcon className="h-6 w-6 text-red-600 dark:text-red-400" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900 dark:text-white">Reject Application</h3>
                </div>
                
                <p className="text-gray-600 dark:text-gray-300 mb-4">
                  Send a polite rejection message to <strong>{selectedApplication?.applicant?.first_name}</strong>.
                </p>
                
                <textarea
                  value={actionMessage}
                  onChange={(e) => setActionMessage(e.target.value)}
                  placeholder="Thank you for your interest..."
                  rows={4}
                  className="w-full px-4 py-3 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-lg focus:ring-2 focus:ring-red-500 focus:border-transparent mb-6 resize-none"
                />
                
                <div className="flex justify-end gap-3">
                  <Button variant="outline" onClick={() => setShowRejectModal(false)} className="dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700">
                    Cancel
                  </Button>
                  <Button
                    variant="outline"
                    onClick={handleRejectApplication}
                    loading={processing}
                    className="text-red-600 border-red-200 hover:bg-red-50"
                  >
                    Send Rejection
                  </Button>
                </div>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default ReviewApplicantsPage;
